import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getPersonaCatalog,
  getProfile,
  updateCoachGeneralEnabled,
  updateCoachWorkoutEnabled,
  updatePersona,
} from '../api';
import type { PersonaCard, UserProfile } from '../types';
import { Spinner } from '../components/Spinner';

export function Coach() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [catalog, setCatalog] = useState<PersonaCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [generalBusy, setGeneralBusy] = useState(false);
  const [workoutBusy, setWorkoutBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getProfile(), getPersonaCatalog()])
      .then(([p, c]) => {
        if (cancelled) return;
        setProfile(p);
        setCatalog(c);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentPersona: PersonaCard | null = useMemo(() => {
    const slug = profile?.coach_personality ?? null;
    if (!slug) return catalog.find((p) => p.slug === 'supportive') ?? catalog[0] ?? null;
    return catalog.find((p) => p.slug === slug) ?? null;
  }, [profile, catalog]);

  const generalEnabled = profile?.coach_general_enabled ?? true;
  const workoutEnabled = profile?.coach_workout_enabled ?? true;

  const onSelectPersona = async (slug: string): Promise<void> => {
    if (!profile) return;
    const previous = profile.coach_personality;
    setProfile({ ...profile, coach_personality: slug });
    setPickerOpen(false);
    try {
      await updatePersona(slug);
    } catch (err) {
      setProfile({ ...profile, coach_personality: previous });
      setError(err instanceof Error ? err.message : 'Failed to update persona');
    }
  };

  const onToggleGeneral = async (next: boolean): Promise<void> => {
    if (!profile || generalBusy || next === generalEnabled) return;
    setGeneralBusy(true);
    setError(null);
    const previousEnabled = profile.coach_general_enabled;
    const previousStamp = profile.coach_general_toggled_off_at;
    setProfile({
      ...profile,
      coach_general_enabled: next,
      coach_general_toggled_off_at: next ? null : new Date().toISOString(),
    });
    try {
      const res = await updateCoachGeneralEnabled(next);
      setProfile((p) =>
        p
          ? {
              ...p,
              coach_general_enabled: res.enabled,
              coach_general_toggled_off_at: res.enabled ? null : p.coach_general_toggled_off_at,
            }
          : p,
      );
    } catch (err) {
      setProfile((p) =>
        p
          ? {
              ...p,
              coach_general_enabled: previousEnabled,
              coach_general_toggled_off_at: previousStamp,
            }
          : p,
      );
      setError(err instanceof Error ? err.message : 'Failed to update toggle');
    } finally {
      setGeneralBusy(false);
    }
  };

  const onToggleWorkout = async (next: boolean): Promise<void> => {
    if (!profile || workoutBusy || next === workoutEnabled) return;
    setWorkoutBusy(true);
    setError(null);
    const previousEnabled = profile.coach_workout_enabled;
    const previousStamp = profile.coach_workout_toggled_off_at;
    setProfile({
      ...profile,
      coach_workout_enabled: next,
      coach_workout_toggled_off_at: next ? null : new Date().toISOString(),
    });
    try {
      const res = await updateCoachWorkoutEnabled(next);
      setProfile((p) =>
        p
          ? {
              ...p,
              coach_workout_enabled: res.enabled,
              coach_workout_toggled_off_at: res.enabled ? null : p.coach_workout_toggled_off_at,
            }
          : p,
      );
    } catch (err) {
      setProfile((p) =>
        p
          ? {
              ...p,
              coach_workout_enabled: previousEnabled,
              coach_workout_toggled_off_at: previousStamp,
            }
          : p,
      );
      setError(err instanceof Error ? err.message : 'Failed to update toggle');
    } finally {
      setWorkoutBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="shell center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="shell">
      <header className="row row--between rise" style={{ marginBottom: 8 }}>
        <div>
          <p className="eyebrow">Coach</p>
          <h1 className="h1">Your coach</h1>
        </div>
        <button type="button" className="btn btn--ghost" onClick={() => navigate('/dashboard')}>
          Close
        </button>
      </header>

      {error ? (
        <div className="errorbar rise" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      <section className="card card--elevated rise" style={{ marginBottom: 12 }}>
        <div className="row row--between" style={{ alignItems: 'center' }}>
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            <div>
              <p className="eyebrow" style={{ margin: 0 }}>Persona</p>
              <p style={{ margin: 0, fontWeight: 600 }}>{currentPersona?.label ?? 'No coach selected'}</p>
            </div>
          </div>
          <button type="button" className="btn btn--ghost" onClick={() => setPickerOpen((o) => !o)}>
            {pickerOpen ? 'Cancel' : 'Change persona'}
          </button>
        </div>

        {pickerOpen ? (
          <div className="stack" style={{ marginTop: 12, gap: 8 }}>
            {catalog.map((p) => {
              const isCurrent = p.slug === currentPersona?.slug;
              return (
                <button
                  key={p.slug}
                  type="button"
                  className={`card${isCurrent ? ' card--elevated' : ''}`}
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    border: isCurrent ? '2px solid currentColor' : undefined,
                    padding: 12,
                  }}
                  onClick={() => void onSelectPersona(p.slug)}
                >
                  <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600 }}>{p.label}</p>
                      <p className="muted" style={{ margin: '2px 0 0', fontSize: 13 }}>
                        {p.tagline}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}
      </section>

      <section className="card card--elevated rise" style={{ marginBottom: 12 }}>
        <div className="row row--between" style={{ alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontWeight: 600 }}>General coach</p>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 14, lineHeight: 1.5 }}>
              Check-ins, follow-ups, and chat. When on, your coach reaches out to keep you on track.
            </p>
          </div>
          <div className="toggle" role="group" aria-label="General coach toggle">
            <button
              type="button"
              className={`toggle__btn${generalEnabled ? ' toggle__btn--on' : ''}`}
              onClick={() => void onToggleGeneral(true)}
              disabled={generalBusy}
              aria-pressed={generalEnabled}
            >
              On
            </button>
            <button
              type="button"
              className={`toggle__btn${!generalEnabled ? ' toggle__btn--on' : ''}`}
              onClick={() => void onToggleGeneral(false)}
              disabled={generalBusy}
              aria-pressed={!generalEnabled}
            >
              Off
            </button>
          </div>
        </div>
      </section>

      <section className="card card--elevated rise" style={{ marginBottom: 12 }}>
        <div className="row row--between" style={{ alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontWeight: 600 }}>Workout coach</p>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 14, lineHeight: 1.5 }}>
              Talks you through sets and rest periods during workouts.
            </p>
          </div>
          <div className="toggle" role="group" aria-label="Workout coach toggle">
            <button
              type="button"
              className={`toggle__btn${workoutEnabled ? ' toggle__btn--on' : ''}`}
              onClick={() => void onToggleWorkout(true)}
              disabled={workoutBusy}
              aria-pressed={workoutEnabled}
            >
              On
            </button>
            <button
              type="button"
              className={`toggle__btn${!workoutEnabled ? ' toggle__btn--on' : ''}`}
              onClick={() => void onToggleWorkout(false)}
              disabled={workoutBusy}
              aria-pressed={!workoutEnabled}
            >
              Off
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
